import { useNavigate } from 'react-router-dom';
import { useColorContext } from '../../contexts/ColorContext';
import { usePaletteContext } from '../../contexts/PaletteContext';
import { ColorInput } from '../ui/ColorInput';
import { Star, Palette } from 'lucide-react';
import { ClipboardDropdown } from './ClipboardDropdown';

const BRAND_STRIPES = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

export function ContextBar() {
  const {
    input, setInput, inputRef,
    result, parseResult,
    getFormatLabel,
    isFavorited, toggleFavorite,
  } = useColorContext();

  const { currentPalette } = usePaletteContext();

  const valid = !!result;
  const nearestNamed = result?.nearestNamed;
  const navigate = useNavigate();

  return (
    <header className="context-bar">
      {/* Brand mark */}
      <button className="context-bar__brand" type="button" onClick={() => navigate('/')} title="Home">
        <div className="context-bar__brand-stripes">
          {BRAND_STRIPES.map((c) => (
            <div key={c} className="context-bar__brand-stripe" style={{ background: c }} />
          ))}
        </div>
        <div className="context-bar__brand-name">Chromis</div>
      </button>

      {/* Status + input */}
      <div className="context-bar__input-col">
        <div className="context-bar__status">
          {valid && parseResult?.success ? (
            <>
              <span className="context-bar__status-valid">
                ● {getFormatLabel(parseResult.detectedFormat)}
              </span>
              {nearestNamed && (
                <span>
                  nearest{' '}
                  <button
                    className="context-bar__nearest-btn"
                    onClick={() => setInput(nearestNamed.displayName)}
                    title={`Use ${nearestNamed.displayName}`}
                    aria-label={`Use color ${nearestNamed.displayName}`}
                  >
                    {nearestNamed.displayName}
                  </button>
                  {' · ΔE '}
                  {nearestNamed.deltaE.toFixed(2)}
                </span>
              )}
            </>
          ) : input.trim() ? (
            <span className="context-bar__status-invalid">
              ● invalid color
            </span>
          ) : (
            <span className="context-bar__status-hint" style={{ color: 'var(--ink-3)' }}>
              Try <code style={{ fontSize: 'inherit', background: 'var(--panel-2)', padding: '1px 4px', borderRadius: 3 }}>dodgerblue</code> or pick from screen →
            </span>
          )}
        </div>
        <ColorInput
          value={input}
          onChange={setInput}
          inputRef={inputRef}
          hideStatus
        />
      </div>

      {/* Active palette chip */}
      {currentPalette && (
        <button
          className="context-bar__palette-chip"
          type="button"
          onClick={() => navigate('/palette')}
          title={`Active palette: ${currentPalette.name}`}
        >
          <div className="context-bar__palette-dots">
            {currentPalette.colors.slice(0, 4).map((c, i) => (
              <span
                key={i}
                className="context-bar__palette-dot"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <span className="context-bar__palette-name">{currentPalette.name}</span>
        </button>
      )}

      {/* Action buttons */}
      <ClipboardDropdown />
      <button
        className={`context-bar__action-btn${isFavorited ? ' context-bar__action-btn--active' : ''}`}
        title={isFavorited ? 'Remove from favorites' : 'Favorite this color'}
        onClick={toggleFavorite}
      >
        <Star size={15} fill={isFavorited ? 'currentColor' : 'none'} />
      </button>
    </header>
  );
}
